import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled/macro';
import { ReactComponent as CloseIcon } from '../svgs/icon-close.svg';
import { motion, AnimatePresence } from 'framer-motion';

let notificationManager = null;
let pendingNotifications = [];
const notificationHideDelay = 5000;

export const setNotificationManager = (manager) => {
  notificationManager = manager;
  if (manager) {
    pendingNotifications.forEach(({ type, message, title }) => {
      manager.showNotification(type, message, title);
    });
    pendingNotifications = [];
  }
};

export const showGlobalNotification = (type = 'error', message, title = null) => {
  if (notificationManager) {
    notificationManager.showNotification(type, message, title);
  } else {
    pendingNotifications.push({ type, message, title });
  }
};

// Context for managing the system notification globally
const SystemNotificationContext = createContext();

const CloseIconSvg = styled(CloseIcon)`
  width: 1rem;
  height: 1rem;
  fill: var(--color-gray);
  cursor: pointer;
  & path {
    fill: var(--color-gray);
  }
`;

const SystemNotification = () => {
  const { notifications, hideNotification } = useContext(SystemNotificationContext);

  const containerStyle = {
    position: 'fixed',
    top: '4px',
    left: '4px',
    right: '4px',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  };

  const getNotificationStyle = (type) => ({
    backgroundColor: '#2d2f3c',
    color: 'white',
    padding: '0.5rem 1rem 0.5rem 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
    fontSize: '0.95rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
    borderRadius: '4px',
    borderLeft: `5px solid ${type === 'error' ? '#dc3545' : '#28a745'}`,
  });

  const textStyle = {
    flex: 1,
    textAlign: 'left',
    fontWeight: 400,
  };

  const notificationVariants = {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, x: 20 },
  };

  return (
    <div style={containerStyle}>
      <AnimatePresence>
        {notifications.map((notification) => (
          <motion.div
            key={notification.id}
            style={getNotificationStyle(notification.type)}
            variants={notificationVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.3 }}
          >
            <div style={textStyle}>
              {notification.title && (
                <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                  {notification.title}
                </div>
              )}
              <div>{notification.message}</div>
            </div>
            <CloseIconSvg onClick={() => hideNotification(notification.id)} />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

export const SystemNotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const dismissTimers = React.useRef({});

  const showNotification = useCallback((type = 'error', message, title = null) => {
    setNotifications((prev) => {
      const existing = prev.find(
        (n) => n.type === type && n.message === message && n.title === title
      );
      if (existing) {
        // Reset the auto-dismiss timer for the existing notification
        clearTimeout(dismissTimers.current[existing.id]);
        dismissTimers.current[existing.id] = setTimeout(() => {
          setNotifications((p) => p.filter((n) => n.id !== existing.id));
          delete dismissTimers.current[existing.id];
        }, notificationHideDelay);
        return prev;
      }

      const id = Date.now() + Math.random();
      dismissTimers.current[id] = setTimeout(() => {
        setNotifications((p) => p.filter((n) => n.id !== id));
        delete dismissTimers.current[id];
      }, notificationHideDelay);
      return [...prev, { id, message, type, title }];
    });
  }, []);

  const hideNotification = useCallback((id) => {
    clearTimeout(dismissTimers.current[id]);
    delete dismissTimers.current[id];
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  // Register the notification manager when provider mounts
  useEffect(() => {
    setNotificationManager({ showNotification });
    return () => setNotificationManager(null);
  }, [showNotification]);

  return (
    <SystemNotificationContext.Provider
      value={{ notifications, showNotification, hideNotification }}
    >
      <SystemNotification />
      {children}
    </SystemNotificationContext.Provider>
  );
};
